import { useMutation, useQuery } from '@tanstack/react-query';
import { App, Select } from 'antd';
import { X } from 'lucide-react';
import { useState } from 'react';
import type {
  CommandFamiliesResultDto,
  CommandHandDownOptionsDto,
  FillCommandFamilyDto,
  FillCommandSeatDto,
  OrgUnitSummaryDto,
} from '@pams/shared';
import { seatIsAwaited } from '@pams/shared';
import { OfficerPicker } from '@/components/OfficerPicker';
import { api, type ApiError } from '@/lib/api';
import {
  handDownCapacity,
  handDownFloor,
  handDownSeed,
  handDownTargets,
  type HandDownRow,
} from '@/lib/commandFamilies';
import type { FillControls } from '@/lib/fillPermissions';
import { queryKeys } from '@/lib/queryKeys';
import { cn } from '@/lib/utils';
import { COMMAND_FAMILY_COPY } from './copy';
import { DelegationRowsEditor } from './DelegationRowsEditor';

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  [C6a §4] THE COMMAND ROLES A DIVISION WAS ASKED FOR, ABOVE THE FORCE GRID.
 *
 *  One FAMILY per role per asked division (`fillCommandFamilySchema`), and one CHIP per SEAT — a seat
 *  is one position: it holds a name, or it is `Awaited` (`Not named` once this duty can take no name).
 *  The division names an officer into an empty
 *  seat, clears a name, or hands empty seats to its own units.
 *
 *  ⚠️ NOTHING HERE IS A NUMBER, AND NOTHING HERE IS A FORCE ROW. No `N of M`, no count beside a role —
 *  a command position is never a figure (FR-5.2.6b) — and none of the force grid's handles: no
 *  `fill-line`, `fill-chips`, `fill-post` or `slot-add`, and no `SlotChip`. `fillGaps.spec.ts` reaches
 *  the force grid through those with `.first()`, so a command row rendered ABOVE it wearing one of
 *  them would be measured instead and the spec would pass over the wrong box. The ids here are
 *  `command-family-*` and `command-seat-*`, and the editor's rows are `data-command-delegate-row`.
 *
 *  ⚠️ IT HOLDS ITS OWN WRITES AND NEVER THE SHEET. Every verb answers with the caller's families on
 *  this duty — narrowed by the page's own `?division=`, which every request here carries — and that
 *  answer goes to `onChanged`, where the page writes it onto the sheet with `setQueryData`. Nothing
 *  here invalidates anything (`fillKeyIsolation.test.ts`), and nothing here can reach a force figure,
 *  because the answer holds no force field to write.
 *
 *  ⚠️ AND A REFUSED WRITE CALLS `onRefused`, WHERE THE PAGE RE-READS THE SHEET. A refusal carries no
 *  families, and the sheet is `staleTime: Infinity` with no focus refetch — so a seat a colleague named
 *  meanwhile (or a slower answer that landed last) would go on reading `Awaited` beside a picker, with
 *  no Remove to offer, and every retry would meet the same 409. The force verbs heal exactly this case
 *  the same way: `swap` and `remove` call `refreshSheet` on error (`useFillSheet.ts`).
 *
 *  ⚠️ THE CONTROLS FOLLOW THE FORCE VERBS' OWN GATES. Naming and removing are `controls.mayName` /
 *  `mayRemove` — the capability AND this duty's naming window — and handing on is `mayDelegate` AND
 *  the server's own `family.mayHandDown`, which already refuses a station holding a handed-down seat.
 *  The server re-checks all of it; a closed control here is not offering what the write refuses.
 * ─────────────────────────────────────────────────────────────────────────────────
 */
export type CommandFamiliesProps = {
  dutyId: string;
  /** The page's `?division=`. Every write carries it, so its answer is narrowed exactly as the sheet. */
  division: string | undefined;
  families: readonly FillCommandFamilyDto[];
  controls: FillControls;
  /**
   * ⚠️ [C6a review] REQUIRED — WHETHER THIS DUTY'S NAMING WINDOW IS OPEN, the sheet's own
   * `namingWindowOpen`. An empty seat reads `Awaited` only while a name can still reach this duty; after,
   * `Not named` — the server's `awaitedCommandsOnDuty` rule, which the footer, the printed order and the
   * officer app already follow. Not `controls.mayName`: that also carries the capability, and a
   * read-only operator is still reading a position that is awaited. Required, because a default would
   * put one reading in the omitted position and ship it silently ([C5b §3]'s rule for a word).
   */
  namingOpen: boolean;
  onChanged: (families: FillCommandFamilyDto[]) => void;
  /** A write was refused: the cached families may be stale, so the page re-reads the sheet. REQUIRED. */
  onRefused: () => void;
};

/** A family's own handle: the division, then the role — two families may share either, never both. */
const familyKeyOf = (family: FillCommandFamilyDto): string => `${family.division.shortCode}-${family.commandKey}`;

/**
 * ⚠️ THE BUILDER'S TWO-VALUE SWITCH, NOT `PostModeControl`. That one is an antd `Segmented` whose copy
 * is fixed ("Assign by Name" / "Delegate to PS / Unit") and whose accessible name says "Who fills" a
 * POST — and `fillGaps.spec.ts` finds its Delegate arm by name with `.first()`, so a second one above
 * the grid would be the one it clicked. Two plain `<button aria-pressed>`s, the builder's own
 * `AskModeSwitch` shape, with their own words.
 */
const MODE_BUTTON =
  'inline-flex h-6 items-center whitespace-nowrap rounded px-2 text-xs transition-colors hover:bg-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';

type Mode = 'name' | 'handDown';

export const CommandFamilies = ({
  dutyId,
  division,
  families,
  controls,
  namingOpen,
  onChanged,
  onRefused,
}: CommandFamiliesProps) => {
  const { message, modal } = App.useApp();
  /* Which families show the hand-down editor. A half-made decision, so it is never persisted. */
  const [modes, setModes] = useState<ReadonlyMap<string, Mode>>(new Map());
  const setMode = (key: string, mode: Mode): void => {
    setModes((current) => new Map([...current, [key, mode]]));
  };
  const narrowed = division ? { query: { division } } : undefined;

  const name = useMutation({
    mutationFn: ({ seatId, officerId }: { seatId: string; officerId: string }) =>
      api.post<CommandFamiliesResultDto>(`/duties/${dutyId}/command-seats/${seatId}/officer`, { officerId }, narrowed),
    onSuccess: (result) => {
      onChanged(result.families);
      void message.success(COMMAND_FAMILY_COPY.named);
    },
    onError: (error: ApiError) => {
      void message.error(error.message);
      onRefused();
    },
  });

  const clear = useMutation({
    mutationFn: (seatId: string) =>
      api.delete<CommandFamiliesResultDto>(`/duties/${dutyId}/command-seats/${seatId}/officer`, narrowed),
    onSuccess: (result) => {
      onChanged(result.families);
      void message.success(COMMAND_FAMILY_COPY.cleared);
    },
    onError: (error: ApiError) => {
      void message.error(error.message);
      onRefused();
    },
  });

  const seatChip = (family: FillCommandFamilyDto, seat: FillCommandSeatDto, position: number) => {
    /* A position that sits with a unit — handed on, or asked of it directly — says which. */
    const unitCode = seat.addressee.id === family.division.id ? null : seat.addressee.shortCode;
    const officer = seat.officer;

    if (!seatIsAwaited(seat) && officer) {
      return (
        <span
          data-testid={`command-seat-${seat.seatId}`}
          data-state="named"
          /* ⚠️ No right padding only where the 44px remove control sits flush inside the border. */
          className={cn(
            'inline-flex min-h-11 max-w-full items-center gap-2 rounded-md border border-border bg-card py-0 pl-2 text-sm',
            controls.mayRemove ? 'pr-0' : 'pr-2.5',
          )}
        >
          <span className="min-w-0 truncate">{officer.name}</span>
          {officer.rankShortName ? (
            <span className="shrink-0 text-xs text-muted-foreground">{officer.rankShortName}</span>
          ) : null}
          {unitCode ? <span className="shrink-0 font-mono text-xs text-muted-foreground">{unitCode}</span> : null}
          {controls.mayRemove ? (
            <button
              type="button"
              data-testid={`command-seat-remove-${seat.seatId}`}
              aria-label={COMMAND_FAMILY_COPY.removeLabel(officer.name, family.label)}
              disabled={clear.isPending}
              className="inline-flex size-11 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:text-destructive focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring disabled:opacity-50"
              onClick={() => {
                /* ⚠️ `modal` FROM `App.useApp()`, NEVER THE STATIC — the static escapes `ThemeScope` and
                   does not render at all under jsdom (CLAUDE.md §8). */
                modal.confirm({
                  title: COMMAND_FAMILY_COPY.removeTitle(officer.name, family.label),
                  content: COMMAND_FAMILY_COPY.removeIntro,
                  okText: COMMAND_FAMILY_COPY.removeConfirm,
                  okButtonProps: { danger: true },
                  onOk: () => {
                    clear.mutate(seat.seatId);
                  },
                });
              }}
            >
              <X className="size-4" aria-hidden />
            </button>
          ) : null}
        </span>
      );
    }

    const naming = name.isPending && name.variables?.seatId === seat.seatId;
    return (
      <span className="inline-flex max-w-full flex-wrap items-center gap-2">
        <span
          data-testid={`command-seat-${seat.seatId}`}
          data-state={namingOpen ? 'awaited' : 'not-named'}
          className="inline-flex min-h-11 items-center gap-2 rounded-md border border-dashed border-slot-outline px-2.5 text-sm text-muted-foreground"
        >
          {namingOpen ? COMMAND_FAMILY_COPY.awaited : COMMAND_FAMILY_COPY.notNamed}
          {unitCode ? <span className="font-mono text-xs">{unitCode}</span> : null}
        </span>
        {controls.mayName ? (
          <OfficerPicker
            mode="single"
            loadOnOpen
            /*
             * ⚠️ THE SEAT'S CURRENT ADDRESSEE, THE ROLE'S EXPANDED RANKS, ACTIVE ONLY — the three
             * refusals `nameCommandSeat` makes, asked of the search, so it offers nobody the write
             * turns down ([C2b §5]). A position Central handed to Tala is filled from Tala.
             */
            withinOrgUnitId={seat.addressee.id}
            eligibleRankIds={family.rankIds}
            activeOnly
            disabled={naming}
            placeholder={COMMAND_FAMILY_COPY.namePlaceholder}
            label={COMMAND_FAMILY_COPY.nameLabel(
              family.label,
              unitCode ? `${family.division.shortCode} · ${unitCode}` : family.division.shortCode,
              position,
            )}
            testId={`command-seat-name-${seat.seatId}`}
            onPick={(officerId) => {
              name.mutate({ seatId: seat.seatId, officerId });
            }}
          />
        ) : null}
      </span>
    );
  };

  return (
    <section data-testid="command-families" className="mb-4 flex flex-col">
      <h2 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {COMMAND_FAMILY_COPY.heading}
      </h2>
      {families.map((family) => {
        const key = familyKeyOf(family);
        const offersHandDown = family.mayHandDown && controls.mayDelegate;
        const mode: Mode = offersHandDown ? (modes.get(key) ?? 'name') : 'name';

        return (
          <div
            key={key}
            data-testid={`command-family-${key}`}
            data-command-key={family.commandKey}
            className="flex flex-col gap-2 border-b border-border py-3"
          >
            <div className="flex flex-wrap items-center gap-3">
              <span className="truncate text-sm font-medium">{family.label}</span>
              {/*
                ⚠️ `AddresseeCodes`' REGISTER, NOT THE COMPONENT. That one always states a unit and
                renders `CD · Any` for none — but a family is keyed on the DIVISION alone and may hold
                positions its units carry, so "Any" would be a claim about where they sit. The division's
                code is shown, its name is read out and hovered, and each seat says its own unit.
              */}
              <span
                title={family.division.name}
                className="truncate rounded-md bg-secondary px-1.5 py-1 text-left font-mono text-xs text-muted-foreground"
              >
                <span aria-hidden>{family.division.shortCode}</span>
                <span className="sr-only">{family.division.name}</span>
              </span>
              {offersHandDown ? (
                <span
                  role="group"
                  aria-label={COMMAND_FAMILY_COPY.modeLabel(family.label, family.division.shortCode)}
                  className="inline-flex items-center gap-0.5"
                  data-testid={`command-mode-${key}`}
                >
                  <button
                    type="button"
                    aria-pressed={mode === 'name'}
                    aria-label={COMMAND_FAMILY_COPY.nameHereFor(family.label, family.division.shortCode)}
                    data-testid={`command-mode-name-${key}`}
                    className={cn(MODE_BUTTON, mode === 'name' ? 'bg-active text-foreground' : 'text-muted-foreground')}
                    onClick={() => {
                      setMode(key, 'name');
                    }}
                  >
                    {COMMAND_FAMILY_COPY.nameHere}
                  </button>
                  <button
                    type="button"
                    aria-pressed={mode === 'handDown'}
                    aria-label={COMMAND_FAMILY_COPY.handDownFor(family.label, family.division.shortCode)}
                    data-testid={`command-mode-handdown-${key}`}
                    className={cn(
                      MODE_BUTTON,
                      mode === 'handDown' ? 'bg-active text-foreground' : 'text-muted-foreground',
                    )}
                    onClick={() => {
                      setMode(key, 'handDown');
                    }}
                  >
                    {COMMAND_FAMILY_COPY.handDown}
                  </button>
                </span>
              ) : null}
            </div>

            {mode === 'handDown' ? (
              <HandDownEditor
                dutyId={dutyId}
                familyKey={key}
                family={family}
                query={narrowed}
                onCancel={() => {
                  setMode(key, 'name');
                }}
                onDone={(next) => {
                  setMode(key, 'name');
                  onChanged(next);
                  void message.success(COMMAND_FAMILY_COPY.handedDown);
                }}
                onRefused={onRefused}
              />
            ) : null}

            <ul className="flex flex-wrap items-center gap-2" data-testid={`command-seats-${key}`}>
              {family.seats.map((seat, index) => (
                <li key={seat.seatId} className="max-w-full">
                  {seatChip(family, seat, index + 1)}
                </li>
              ))}
            </ul>
          </div>
        );
      })}
    </section>
  );
};

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  ⚠️ HAND EMPTY POSITIONS TO UNITS — THE SAME EDITOR AS THE FORCE SPLIT, WITH A UNIT TARGET.
 *
 *  `DelegationRowsEditor`, not a copy of it ([C6a §2d]): stacked rows of (unit, how many), add-another
 *  beneath, Confirm/Cancel on the first row, a seed latched on a fingerprint. Seeded from what this
 *  family has ALREADY handed on, so confirming it unchanged moves nothing.
 *
 *  ⚠️ NO CEILING, POOL OR FLOOR SENTENCE. Those captions are the force host's; a command host passes
 *  none, and the bounds live in the predicates — the per-row floor is the names already standing with
 *  that unit (a named seat never moves), the upper bound is the positions that could move.
 *
 *  ⚠️ CONFIRM DECIDES WHICH EMPTY SEATS GO WHERE (`handDownTargets`) — the server moves seats by id —
 *  and posts only the seats whose place changes. The unit list is the server's own
 *  (`GET …/command-seats/options`: the division's active units less any the order asks directly), so
 *  the picker never offers a unit the write refuses.
 *
 *  ⚠️ AND IT SAYS WHAT THE WRITE REACHES before the click — every duty of this duty point still open
 *  for naming — because the seat is the card's, not tonight's.
 *
 *  ⚠️ A REFUSED CONFIRM RE-READS THE SHEET AND STILL THROWS. The editor shows the server's sentence
 *  from the throw; `onRefused` replaces the stale families under it, because a hand-down refused as
 *  "Somebody is named on that position" was planned against seats that have since changed.
 * ─────────────────────────────────────────────────────────────────────────────────
 */
const HandDownEditor = ({
  dutyId,
  familyKey,
  family,
  query,
  onCancel,
  onDone,
  onRefused,
}: {
  dutyId: string;
  familyKey: string;
  family: FillCommandFamilyDto;
  query: { query: { division: string } } | undefined;
  onCancel: () => void;
  onDone: (families: FillCommandFamilyDto[]) => void;
  onRefused: () => void;
}) => {
  const options = useQuery({
    queryKey: queryKeys.fill.commandSeats(dutyId, family.commandKey, family.division.id),
    queryFn: ({ signal }) =>
      api.get<CommandHandDownOptionsDto>(`/duties/${dutyId}/command-seats/options`, {
        query: { commandKey: family.commandKey, divisionOrgUnitId: family.division.id },
        signal,
      }),
  });

  const prefix = `command-handdown-${familyKey}`;
  const offered = options.data?.units ?? [];
  /*
   * ⚠️ A UNIT ALREADY HOLDING A POSITION IS ALWAYS NAMEABLE IN ITS OWN ROW, even if the options no
   * longer list it — otherwise its seeded row would paint an ObjectId (divergence #84).
   */
  const known = new Map<string, OrgUnitSummaryDto>([
    ...family.seats.filter((seat) => seat.handedDown).map((seat) => [seat.addressee.id, seat.addressee] as const),
    ...offered.map((unit) => [unit.id, unit] as const),
  ]);
  const unitName = (id: string | null): string => (id ? (known.get(id)?.name ?? '') : '');
  const capacity = handDownCapacity(family);
  const positionsOf = (rows: readonly HandDownRow[]): number =>
    rows.reduce((running, row) => running + (row.target === null ? 0 : row.count), 0);

  return (
    <DelegationRowsEditor
      /* ⚠️ `error` BEFORE `loading`, or a refusal renders as a spinner for ever. */
      error={options.isError ? options.error.message : null}
      loading={options.isPending}
      loadingText={COMMAND_FAMILY_COPY.loading}
      seed={handDownSeed(family)}
      empty={options.data && offered.length === 0 ? COMMAND_FAMILY_COPY.noUnits : undefined}
      renderTarget={({ index, value, disabledKeys, onChange }) => (
        <Select
          size="small"
          showSearch
          /* ⚠️ `label`, NEVER the default — rc-select otherwise filters on the VALUE, an ObjectId. */
          optionFilterProp="label"
          className="w-44"
          placeholder={COMMAND_FAMILY_COPY.pickUnit}
          aria-label={COMMAND_FAMILY_COPY.unitLabel(family.label, index + 1)}
          data-testid={`${prefix}-unit-${String(index)}`}
          value={value}
          options={[...known.values()].map((unit) => ({
            value: unit.id,
            label: unit.name,
            disabled: disabledKeys.has(unit.id),
          }))}
          onChange={(next: string) => {
            onChange(next);
          }}
        />
      )}
      minFor={(target) => handDownFloor(family, target)}
      max={capacity}
      canAddRow={(rows) => {
        const last = rows[rows.length - 1];
        const chosen = rows.flatMap((row) => (row.target ? [row.target] : []));
        return (
          last !== undefined &&
          last.target !== null &&
          last.count > 0 &&
          chosen.length < known.size &&
          positionsOf(rows) < capacity
        );
      }}
      canConfirm={(rows) => (handDownTargets(family, rows) ?? [])[0] !== undefined}
      onConfirm={async (rows) => {
        const targets = handDownTargets(family, rows);
        if (!targets || targets[0] === undefined) return;
        let result: CommandFamiliesResultDto;
        try {
          result = await api.post<CommandFamiliesResultDto>(
            `/duties/${dutyId}/command-seats/hand-down`,
            { commandKey: family.commandKey, divisionOrgUnitId: family.division.id, targets },
            query,
          );
        } catch (refusal) {
          onRefused();
          throw refusal;
        }
        onDone(result.families);
      }}
      onCancel={onCancel}
      testIdPrefix={prefix}
      rowAttribute="data-command-delegate-row"
      copy={{
        confirm: COMMAND_FAMILY_COPY.confirm,
        cancel: COMMAND_FAMILY_COPY.cancel,
        addRow: COMMAND_FAMILY_COPY.addUnit,
        refused: COMMAND_FAMILY_COPY.refused,
        /* ⚠️ A ROW WITH NO UNIT YET IS NAMED BY ITS ROW, or two blank rows would share one name. */
        countLabel: (row, index) =>
          COMMAND_FAMILY_COPY.countFor(family.label, unitName(row.target) || `row ${String(index + 1)}`),
      }}
      footer={
        <p className="text-xs text-muted-foreground" data-testid={`${prefix}-reach`}>
          {COMMAND_FAMILY_COPY.reach}
        </p>
      }
    />
  );
};
